/** @jsx jsx */
import { Button, Grid, IconButton, Spacer } from "@cd2/components/build/es6"
import { Fields, Form2 } from "@cd2/forms/build/es6"
import { P } from "@cd2/text/build/es6"
import { jsx } from "@emotion/core"
import React from "react"
import { icons } from "../../constants/icons"
import { spliceArray } from "../../utils/arrays"
import { IWriteOffCategorySetting } from "../../utils/useSettings"

/**
 * The write off categories a branch can pick from when exporting a stock take write off, each
 * paired with the nominal code the write off is posted against.
 */
export function WriteOffCategoriesField({ field }: { field: string }) {
  const formField = Form2.useField<IWriteOffCategorySetting[]>(field, {
    subscribe: true,
    defaultValue: [],
  })

  const categories = formField.getValue()

  Form2.useFormSubmitLock(() => {
    const hasIncomplete = categories.some(
      category => !category.value?.trim() || !category.nominal?.trim(),
    )
    if (hasIncomplete) {
      formField.setError("Every category needs both a name and a nominal code")
      return false
    }
    const hasDuplicates = categories.some(
      category =>
        categories.filter(other => other.value.trim() === category.value.trim()).length > 1,
    )
    if (hasDuplicates) {
      formField.setError("Categories must be unique. Change or remove the duplicate rows.")
      return false
    }
    const hasInvalidNominal = categories.some(category => !/^\d+$/.test(category.nominal.trim()))
    if (hasInvalidNominal) {
      formField.setError("Nominal codes can only contain numbers")
      return false
    }
    formField.setError(undefined)
  })

  function handleChange(index: number, partial: Partial<IWriteOffCategorySetting>) {
    formField.setValue(spliceArray(categories, index, 1, { ...categories[index], ...partial }))
  }

  return (
    <div>
      {categories.length > 0 && (
        <Grid columns={[{ fr: 1 }, 120, "max-content"]} gap={8} alignItems="end">
          {categories.map((category, index) => (
            <React.Fragment key={category.key}>
              <Fields.Text
                label={index === 0 ? "Category" : undefined}
                value={category.value}
                onChange={value => handleChange(index, { value })}
                placeholder="e.g. Damaged Stock"
                error={!category.value?.trim()}
              />
              <Fields.Text
                label={index === 0 ? "Nominal" : undefined}
                value={category.nominal}
                // typed and pasted values are stripped to digits, so a nominal can keep any
                // leading zeros without ever holding something Sage would not accept
                onChange={nominal => handleChange(index, { nominal: nominal.replace(/\D/g, "") })}
                placeholder="e.g. 5154"
                error={!/^\d+$/.test(category.nominal?.trim() ?? "")}
              />
              <IconButton
                icon={icons.delete}
                buttonColor="red"
                onClick={() => formField.setValue(spliceArray(categories, index, 1))}
                square
              />
            </React.Fragment>
          ))}
        </Grid>
      )}
      <Spacer size={8} />
      <Button
        leftIcon={icons.plus}
        onClick={() =>
          formField.setValue([
            ...categories,
            { key: Math.random().toString(36), value: "", nominal: "" },
          ])
        }
      >
        Add Category
      </Button>
      {formField.getError() && <P color="error">{formField.getError()}</P>}
    </div>
  )
}
